/**
 * Centralized constants for the Risk Game application
 * All magic numbers and configuration values should be defined here
 */

// ===== Time Constants (in seconds unless specified) =====
export const TIME_CONSTANTS = {
  // Incident time limits
  DEFAULT_INCIDENT_TIME_LIMIT: 600, // 10 minutes
  MINIMUM_TIME_LIMIT: 60, // 1 minute
  MAXIMUM_TIME_LIMIT: 1200, // 20 minutes
  
  // Timer warnings
  TIMER_WARNING_30_SECONDS: 30,
  TIMER_WARNING_10_SECONDS: 10,
  
  // Animations and transitions (in milliseconds)
  ANIMATION_DURATION: 300,
  TOAST_DURATION: 3000,
  DEBOUNCE_DELAY: 300,
  LOADING_DELAY: 100,
  
  // Auto-save and persistence
  AUTO_SAVE_INTERVAL: 30000, // 30 seconds
  SESSION_TIMEOUT: 1800000, // 30 minutes
} as const;

// ===== Investment Constants =====
export const INVESTMENT_CONSTANTS = {
  // Cost tiers
  COST_TIER_1: 50000,   // Basic investments
  COST_TIER_2: 100000,  // Standard investments
  COST_TIER_3: 250000,  // Advanced investments
  COST_TIER_4: 500000,  // Premium investments
  COST_TIER_5: 1000000, // Enterprise investments
  
  // Budget limits
  MIN_BUDGET: 100000,
  DEFAULT_BUDGET: 1000000,
  MAX_BUDGET: 10000000,
  
  // Selection limits
  MIN_INVESTMENTS: 1,
  DEFAULT_MAX_INVESTMENTS: 5,
  MAX_INVESTMENTS: 10,
  
  // Impact ranges (0-100)
  MIN_IMPACT: 0,
  MAX_IMPACT: 100,
  DEFAULT_IMPACT: 50,
  
  // ROI percentages
  MIN_ROI: -50,
  MAX_ROI: 300,
  DEFAULT_ROI: 100,
  
  // Implementation time (days)
  MIN_IMPLEMENTATION_TIME: 1,
  MAX_IMPLEMENTATION_TIME: 365,
  DEFAULT_IMPLEMENTATION_TIME: 30,
} as const;

// ===== Scoring Constants =====
export const SCORING_CONSTANTS = {
  // Base scores
  BASE_SCORE_PER_CATEGORY: 100,
  MAX_SCORE: 1000,
  MIN_SCORE: 0,
  
  // Grade thresholds
  GRADE_THRESHOLDS: {
    WISE: 90,
    EXCELLENT: 80,
    GOOD: 70,
    DECENT: 50,
    POOR: 30,
    FAILING: 0,
  },
  
  // Score multipliers
  PERFECT_SCORE_MULTIPLIER: 1.5,
  SYNERGY_MULTIPLIER: 1.2,
  PENALTY_MULTIPLIER: 0.8,
  
  // Response time bonus (no longer used, kept for reference)
  RESPONSE_TIME_BONUS: 0,
  
  // Category weights
  DEFAULT_CATEGORY_WEIGHTS: {
    security: 0.25,
    reputation: 0.25,
    financial: 0.25,
    operational: 0.25,
  },
} as const;

// ===== UI Constants =====
export const UI_CONSTANTS = {
  // Pagination
  DEFAULT_PAGE_SIZE: 10,
  MAX_PAGE_SIZE: 100,
  
  // Grid layouts
  SCENARIO_GRID_COLUMNS: {
    mobile: 1,
    tablet: 2,
    desktop: 3,
    wide: 4,
  },
  
  // Z-index layers
  Z_INDEX: {
    base: 0,
    dropdown: 10,
    sticky: 20,
    modal: 30,
    popover: 40,
    tooltip: 50,
    toast: 60,
  },
  
  // Breakpoints (pixels)
  BREAKPOINTS: {
    mobile: 480,
    tablet: 768,
    desktop: 1024,
    wide: 1280,
  },
  
  // Max lengths
  MAX_NAME_LENGTH: 100,
  MAX_DESCRIPTION_LENGTH: 500,
  MAX_LONG_DESCRIPTION_LENGTH: 2000,
  MAX_TAG_LENGTH: 30,
  MAX_TAGS: 10,
} as const;

// ===== Game Flow Constants =====
export const GAME_FLOW_CONSTANTS = {
  // Phase durations (milliseconds)
  PHASE_TRANSITION_DELAY: 500,
  INTRO_MIN_DURATION: 3000,
  
  // Retry limits
  MAX_RETRIES: 3,
  RETRY_DELAY: 1000,
  
  // Session limits
  MAX_INCIDENTS_PER_SESSION: 20,
  MIN_INCIDENTS_PER_SESSION: 3,
  
  // Score calculation
  INCIDENT_WEIGHT: 0.7,
  INVESTMENT_WEIGHT: 0.3,
} as const;

// ===== Storage Constants =====
export const STORAGE_CONSTANTS = {
  // Local storage keys
  STORAGE_KEYS: {
    SCENARIOS: 'riskgame_scenarios',
    INVESTMENTS: 'riskgame_investments',
    INCIDENTS: 'riskgame_incidents',
    SCORING_CONFIG: 'riskgame_scoring_config',
    USER_PREFERENCES: 'riskgame_preferences',
    THEME: 'cybersec-risk-theme',
  },
  
  // Storage limits
  MAX_STORAGE_ENTRIES: 1000,
  MAX_BACKUP_AGE_DAYS: 7,
  
  // Export/Import
  EXPORT_VERSION: '1.0.0',
  SUPPORTED_IMPORT_VERSIONS: ['1.0.0'],
} as const;

// ===== Validation Constants =====
export const VALIDATION_CONSTANTS = {
  // Field requirements
  MIN_PASSWORD_LENGTH: 8,
  MAX_PASSWORD_LENGTH: 128,
  
  // Numeric ranges
  MIN_PERCENTAGE: 0,
  MAX_PERCENTAGE: 100,
  
  // Date ranges
  MIN_YEAR: 2020,
  MAX_YEAR: 2030,
  
  // File upload
  MAX_FILE_SIZE: 10485760, // 10MB
  ALLOWED_FILE_TYPES: ['.json', '.csv', '.xlsx'],
} as const;

// ===== Error Constants =====
export const ERROR_CONSTANTS = {
  // Retry configuration
  ERROR_RETRY_ATTEMPTS: 3,
  ERROR_RETRY_DELAY: 1000,
  
  // Timeout values
  REQUEST_TIMEOUT: 30000,
  LONG_REQUEST_TIMEOUT: 60000,
  
  // Error codes
  ERROR_CODES: {
    NETWORK_ERROR: 'NETWORK_ERROR',
    VALIDATION_ERROR: 'VALIDATION_ERROR',
    AUTH_ERROR: 'AUTH_ERROR',
    SERVER_ERROR: 'SERVER_ERROR',
    UNKNOWN_ERROR: 'UNKNOWN_ERROR',
  },
} as const;

// ===== Performance Constants =====
export const PERFORMANCE_CONSTANTS = {
  // Debounce/throttle delays
  SEARCH_DEBOUNCE: 300,
  SCROLL_THROTTLE: 100,
  RESIZE_DEBOUNCE: 200,
  
  // Cache durations (milliseconds)
  CACHE_DURATION: 300000, // 5 minutes
  LONG_CACHE_DURATION: 3600000, // 1 hour
  
  // Performance thresholds
  SLOW_RENDER_THRESHOLD: 16, // 60fps = 16ms per frame
  SLOW_API_THRESHOLD: 1000,
} as const;

// Type exports for better type safety
export type TimeConstants = typeof TIME_CONSTANTS;
export type InvestmentConstants = typeof INVESTMENT_CONSTANTS;
export type ScoringConstants = typeof SCORING_CONSTANTS;
export type UIConstants = typeof UI_CONSTANTS;
export type GameFlowConstants = typeof GAME_FLOW_CONSTANTS;
export type StorageConstants = typeof STORAGE_CONSTANTS;
export type ValidationConstants = typeof VALIDATION_CONSTANTS;
export type ErrorConstants = typeof ERROR_CONSTANTS;
export type PerformanceConstants = typeof PERFORMANCE_CONSTANTS;